import { z } from "zod";

/**
 * A rule's `type` selects the server-side matcher, and the valid set is
 * whatever is registered in `BanRuleMatcherRegistry` — not a closed list the
 * client can know. It is parsed as a plain string so registering a new matcher
 * never breaks this client; the constant below is only what the UI offers.
 */
export const BAN_RULE_TYPE_PROJECT_TEXT = "project_text";

/** `match_mode` is the `project_text` matcher's own knob, fixed in the model. */
export const BanRuleMatchModeSchema = z.enum(["word", "substring"]);

export const BannedEmailSourceSchema = z.enum(["rule", "manual"]);

/** `repeat` marks an address seen again from an already-banned submitter. */
export const BannedIpSourceSchema = z.enum(["rule", "manual", "repeat"]);

export type BanRuleMatchMode = z.infer<typeof BanRuleMatchModeSchema>;
export type BannedEmailSource = z.infer<typeof BannedEmailSourceSchema>;
export type BannedIpSource = z.infer<typeof BannedIpSourceSchema>;

/** The trimmed rule the API embeds on ban records. */
export const BanRuleReferenceSchema = z.object({
    id: z.number(),
    name: z.string(),
});

export const BanRuleSchema = z.object({
    id: z.number(),
    type: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    terms: z.array(z.string()),
    match_mode: BanRuleMatchModeSchema,
    is_active: z.boolean(),
    hits_count: z.number().int().min(0),
    last_matched_at: z.string().nullable(),
    banned_emails_count: z.number().int().min(0),
    banned_ips_count: z.number().int().min(0),
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
});

/**
 * Submission audit trail (source endpoint, record ids). PHP serializes an empty
 * context as `[]` rather than `{}`, so normalize that to null before parsing.
 */
const BanContextSchema = z.preprocess(
    (value) => (Array.isArray(value) && value.length === 0 ? null : value),
    z.record(z.string(), z.unknown()).nullable(),
);

export const BannedEmailSchema = z.object({
    id: z.number(),
    email: z.string(),
    source: BannedEmailSourceSchema,
    ban_rule: BanRuleReferenceSchema.nullable(),
    matched_terms: z.array(z.string()),
    matched_text: z.string().nullable(),
    ip_address: z.string().nullable(),
    context: BanContextSchema,
    notes: z.string().nullable(),
    user_id: z.number().nullable(),
    banned_at: z.string().nullable(),
    created_at: z.string().nullable(),
});

export const BannedIpSchema = z.object({
    id: z.number(),
    ip_address: z.string(),
    email: z.string().nullable(),
    source: BannedIpSourceSchema,
    ban_rule: BanRuleReferenceSchema.nullable(),
    hits_count: z.number().int().min(0),
    notes: z.string().nullable(),
    first_seen_at: z.string().nullable(),
    last_seen_at: z.string().nullable(),
    created_at: z.string().nullable(),
});

/** Dry-run result: nothing is banned, recorded, or counted. */
export const BanRuleTestResultSchema = z.object({
    matched: z.boolean(),
    ban_rule: z
        .object({
            id: z.number(),
            type: z.string(),
            name: z.string(),
        })
        .nullable(),
    matched_terms: z.array(z.string()),
    matched_text: z.string().nullable(),
});

export const CreateBanRuleRequestSchema = z.object({
    type: z.string().optional(),
    name: z.string().min(1).max(255),
    description: z.string().max(2000).nullable().optional(),
    terms: z.array(z.string().min(1).max(255)).min(1).max(25),
    match_mode: BanRuleMatchModeSchema.optional(),
    is_active: z.boolean().optional(),
});

export const UpdateBanRuleRequestSchema = z.object({
    type: z.string().optional(),
    name: z.string().min(1).max(255).optional(),
    description: z.string().max(2000).nullable().optional(),
    terms: z.array(z.string().min(1).max(255)).min(1).max(25).optional(),
    match_mode: BanRuleMatchModeSchema.optional(),
    is_active: z.boolean().optional(),
});

/** All fields optional — a rule whose type screens a missing field never fires. */
export const TestBanRuleRequestSchema = z.object({
    text: z.string().max(65535).nullable().optional(),
    email: z.string().max(255).nullable().optional(),
    ip: z.string().max(45).nullable().optional(),
});

export const CreateBannedEmailRequestSchema = z.object({
    email: z.string().min(1).max(255),
    notes: z.string().max(2000).nullable().optional(),
    ban_rule_id: z.number().int().nullable().optional(),
});

export const CreateBannedIpRequestSchema = z.object({
    ip_address: z.string().min(1).max(45),
    email: z.string().max(255).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    ban_rule_id: z.number().int().nullable().optional(),
});

export type BanRule = z.infer<typeof BanRuleSchema>;
export type BanRuleReference = z.infer<typeof BanRuleReferenceSchema>;
export type BannedEmail = z.infer<typeof BannedEmailSchema>;
export type BannedIp = z.infer<typeof BannedIpSchema>;
export type BanRuleTestResult = z.infer<typeof BanRuleTestResultSchema>;
export type CreateBanRuleRequest = z.input<typeof CreateBanRuleRequestSchema>;
export type UpdateBanRuleRequest = z.input<typeof UpdateBanRuleRequestSchema>;
export type TestBanRuleRequest = z.input<typeof TestBanRuleRequestSchema>;
export type CreateBannedEmailRequest = z.input<typeof CreateBannedEmailRequestSchema>;
export type CreateBannedIpRequest = z.input<typeof CreateBannedIpRequestSchema>;
